import { store } from '../store.js';
import {
  saveAppState,
  clearAppState,
  archiveAppState,
  loadArchiveState
} from '../storage.js';
import { getDefaultCurrentPoint } from './useMigration.js';

/**
 * Scoring composable — confirm, undo, set progression, match completion.
 * Bridge pattern: receives component instance during Options API phase.
 */
export function useScoring(instance) {
  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function getSetWinner(score) {
    if (score.won >= 25 && score.won - score.lost >= 2) {
      return 'won';
    }
    if (score.lost >= 25 && score.lost - score.won >= 2) {
      return 'lost';
    }
    return null;
  }

  function handleSetCompletion() {
    const wonSets = store.match.sets.filter(set => set.winner === 'won').length;
    const lostSets = store.match.sets.filter(set => set.winner === 'lost').length;

    if (wonSets >= 3) {
      store.setMatchFinished('won');
      instance.startPointDisabled = true;
      instance.showSetSummary = true;
      return;
    }

    if (lostSets >= 3) {
      store.setMatchFinished('lost');
      instance.startPointDisabled = true;
      instance.showSetSummary = true;
      return;
    }

    if (store.match.sets.length >= 5) {
      store.setMatchFinished('draw');
      instance.startPointDisabled = true;
      instance.showSetSummary = true;
      return;
    }

    instance.showSetSummary = true;
    instance.startPointDisabled = true;
  }

  function confirmSelections() {
    if (instance.matchFinished) {
      return;
    }
    if (instance.currentPoint.scoreAction.outcome === 'Won' && instance.currentPoint.startPoint === 'Receive') {
      store.rotatePlayers();
    }

    if (instance.currentPoint.scoreAction.outcome === 'Won') {
      instance.score.won += 1;
    } else if (instance.currentPoint.scoreAction.outcome === 'Lost') {
      instance.score.lost += 1;
    }

    instance.currentPoint.currentScore = { ...instance.score };
    instance.currentPoint.duringRallyRoster = instance._currentPoint.buildDuringRallyRosterMap();

    instance.lastConfirmedPointId = instance.currentPoint.id;
    console.log('Set lastConfirmedPointId to:', instance.lastConfirmedPointId, 'outcome:', instance.currentPoint.scoreAction.outcome);

    store.addPointToCurrentSet(JSON.parse(JSON.stringify(instance.currentPoint)));

    const setWinner = getSetWinner(instance.score);
    if (setWinner) {
      store.endCurrentSet(setWinner);
      handleSetCompletion();
      scrollToTop();
      return;
    }

    instance.showPointOutcome(instance.currentPoint.scoreAction.outcome);

    instance._currentPoint.incrementCurrentPointValues();
    instance._currentPoint.resetCurrentPointState();
    scrollToTop();
  }

  function autoConfirm() {
    if (instance.autoConfirming || instance.isUndoing || instance.processingTerminalAction) {
      return;
    }
    instance.autoConfirming = true;
    instance.$nextTick(() => {
      instance.$nextTick(() => {
        if (!instance.showSetSummary && !instance.matchFinished) {
          confirmSelections();
        }
        instance.$nextTick(() => {
          instance.autoConfirming = false;
        });
      });
    });
  }

  function getSetStartServeReceive(setNumber) {
    if (setNumber === 5 && store.match.setsWon === 2 && store.match.setsLost === 2) {
      return instance.matchTiebreakServeReceive || '';
    }
    if (!instance.matchStartServeReceive) {
      return '';
    }
    const isOdd = setNumber % 2 === 1;
    if (isOdd) {
      return instance.matchStartServeReceive;
    }
    return instance.matchStartServeReceive === 'Serve' ? 'Receive' : 'Serve';
  }

  function resetForNewSet() {
    instance.score = { won: 0, lost: 0 };
    instance.currentPoint.id = 1;
    instance.currentPoint.startPoint = '';
    instance.currentPoint.currentScore = { won: 0, lost: 0 };
    instance.currentPoint.currentRotationCode = '';
    instance.currentPoint.currentOnCourtNumbers = [];
    instance.currentPoint.currentLineupNumbers = [];
    instance.currentPoint.currentLiberoNumber = null;
    const isTieBreak = store.match.setsWon === 2 && store.match.setsLost === 2;
    const setStart = getSetStartServeReceive(instance.currentSetNumber);
    if (isTieBreak) {
      instance.currentPoint.startPoint = instance.matchTiebreakServeReceive || '';
    } else if (setStart) {
      instance.currentPoint.startPoint = setStart;
    }
    instance.currentLineup = [];
    instance.startingPlayersConfirmed = false;
    instance.startPointDisabled = false;
    store.resetCurrentLineup();

    instance._currentPoint.resetCurrentPointState(false);
  }

  function startNextSet() {
    if (instance.matchFinished) {
      return;
    }
    instance.showSetSummary = false;
    store.startNextSet();
    resetForNewSet();
  }

  function undoLastConfirmedPoint() {
    console.log('undoLastConfirmedPoint called');
    instance.isUndoing = true;
    instance.lastConfirmedPointId = null;

    const currentSet = store.getCurrentSet();
    console.log('currentSet:', currentSet);
    console.log('points length:', currentSet?.points?.length);

    if (!currentSet || !Array.isArray(currentSet.points) || !currentSet.points.length) {
      console.log('Early return - no points to undo');
      instance.isUndoing = false;
      return;
    }

    const removedPoint = currentSet.points[currentSet.points.length - 1];
    console.log('Removing point:', removedPoint);
    currentSet.points = currentSet.points.slice(0, -1);
    console.log('Points after removal:', currentSet.points.length);

    if (currentSet.finished) {
      if (currentSet.winner === 'won') {
        store.match.setsWon = Math.max(0, store.match.setsWon - 1);
      } else if (currentSet.winner === 'lost') {
        store.match.setsLost = Math.max(0, store.match.setsLost - 1);
      }
      currentSet.finished = false;
      currentSet.winner = null;
    }

    store.match.finished = false;
    store.match.winner = null;

    const lastPoint = currentSet.points[currentSet.points.length - 1];
    const previousScore = lastPoint ? lastPoint.currentScore : { won: 0, lost: 0 };
    instance.score = { ...previousScore };
    currentSet.score = { ...previousScore };

    if (removedPoint && removedPoint.scoreAction?.outcome === 'Won' && removedPoint.startPoint === 'Receive') {
      store.currentLineup.forEach(player => {
        if (player.rotationPosition === 6) {
          player.rotationPosition = 1;
        } else {
          player.rotationPosition += 1;
        }
      });
    }

    instance.currentPoint = JSON.parse(JSON.stringify(removedPoint));

    // Restore store.currentLineup players to match the point's lineup numbers
    // (substitutions may have changed store.currentLineup since this point was played)
    const lineupNumbers = instance.currentPoint.currentLineupNumbers;
    if (Array.isArray(lineupNumbers) && lineupNumbers.length === 6) {
      const rosterByNumber = new Map(store.match.roster.map(p => [p.number, p]));
      const sortedLineup = store.currentLineup.slice().sort((a, b) => a.rotationPosition - b.rotationPosition);
      sortedLineup.forEach((item, index) => {
        const expectedNumber = lineupNumbers[index];
        if (item.player && item.player.number !== expectedNumber) {
          const correctPlayer = rosterByNumber.get(expectedNumber);
          if (correctPlayer) {
            item.player = correctPlayer;
          }
        }
      });
    }

    // Restore store.currentLibero to match the point's libero number
    if (instance.currentPoint.currentLiberoNumber != null) {
      const libero = store.match.roster.find(p => p.number === instance.currentPoint.currentLiberoNumber);
      if (libero) {
        store.currentLibero = libero;
      }
    }

    const pointDetail = removedPoint.scoreAction?.pointDetail;

    instance.currentPoint.scoreAction = {
      outcome: null,
      pointDetail: '',
      player: {},
      secondPlayer: null
    };

    if (pointDetail === 'Ace' || pointDetail === 'Serve error') {
      instance.currentPoint.serveAction.rating = null;
    }
    if (pointDetail === 'Opposition Rotation Error' && removedPoint.serveAction?.call) {
      instance.currentPoint.serveAction.call = null;
    }
    if (pointDetail === 'Rotation Error' && removedPoint.serveAction?.call) {
      instance.currentPoint.serveAction.call = null;
    }
    if (pointDetail === 'Opp. ace') {
      instance.currentPoint.receiveAction.rating = null;
    }
    if (pointDetail === 'Opposition Rotation Error' && removedPoint.receiveAction?.call) {
      instance.currentPoint.receiveAction.call = null;
    }
    if (pointDetail === 'Rotation Error' && removedPoint.receiveAction?.call) {
      instance.currentPoint.receiveAction.call = null;
    }
    if (pointDetail === 'Opp. serve error' || pointDetail === 'Team receive error') {
      instance.currentPoint.receiveAction = {
        player: null,
        call: null,
        rating: null
      };
    }

    delete instance.currentPoint.currentScore;

    instance.updatePreviewScore();
    instance.currentLineup = store.currentLineup.slice().sort((a, b) => a.rotationPosition - b.rotationPosition);
    instance.startingPlayersConfirmed = true;
    instance.startPointDisabled = false;
    instance.showSetSummary = false;

    instance.$nextTick(() => {
      instance.$forceUpdate();
      instance.$nextTick(() => {
        instance.isUndoing = false;
        console.log('Undo complete, isUndoing reset to false');
      });
    });
  }

  function completeMatch() {
    if (!instance.matchFinished) {
      return;
    }
    archiveAppState(instance._persistence.buildPersistedState());
    instance.showSetSummary = false;
    instance.showMatchSummary = true;
    instance.matchSummaryDismissed = false;
    instance.startPointDisabled = true;
  }

  function clearLocalStorage() {
    instance.currentPoint = getDefaultCurrentPoint();
    instance.score = { won: 0, lost: 0 };
    instance.currentLineup = [];
    instance.startingPlayersConfirmed = false;
    instance.matchStartServeReceive = '';
    instance.matchTiebreakServeReceive = '';
    instance.matchRoster = [];
    instance.matchLiberos = [];
    instance.matchReady = false;
    instance.startPointDisabled = true;
    instance.showMatchSummary = false;
    instance.matchSummaryDismissed = false;
    store.resetMatchState();
    clearAppState();
  }

  function archiveAndResetMatch() {
    const ok = window.confirm('Archive and start a new match? This will clear the current match from this view.');
    if (!ok) {
      return;
    }
    archiveAppState(instance._persistence.buildPersistedState());
    clearLocalStorage();
  }

  function restoreArchivedMatch(key) {
    const archivedState = loadArchiveState(key);
    if (!archivedState) {
      return;
    }
    saveAppState(archivedState);
    instance._persistence.loadPersistedState();
    instance.showArchiveManager = false;
    instance.showMatchSummary = false;
    instance.matchSummaryDismissed = false;
  }

  return {
    autoConfirm,
    confirmSelections,
    getSetWinner,
    handleSetCompletion,
    startNextSet,
    undoLastConfirmedPoint,
    resetForNewSet,
    getSetStartServeReceive,
    scrollToTop,
    completeMatch,
    clearLocalStorage,
    archiveAndResetMatch,
    restoreArchivedMatch
  };
}
